import { apiFetch } from './client';
import { useAuth } from '@/lib/auth-store';

export interface VoicemailBoxRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  /** Per-customer sequential ref displayed as `VMB-{N}`. Migration 0089. */
  ref_number: number;
  name: string;
  owner_kind: 'account' | 'caller' | 'ivr_node' | 'extension';
  owner_id: string | null;
  has_pin: boolean;
  notify_email: string | null;
  notify_sms: string | null;
  max_messages: number;
  created_at: string;
  updated_at: string;
}

export interface VoicemailRow {
  id: string;
  box_id: string;
  customer_id: string;
  caller_id: string | null;
  from_number: string | null;
  s3_bucket: string;
  s3_key: string;
  duration_ms: number | null;
  transcription: string | null;
  read_at: string | null;
  created_at: string;
}

export interface CreateBoxBody {
  name: string;
  owner_kind: VoicemailBoxRow['owner_kind'];
  owner_id?: string | null;
  notify_email?: string | null;
  notify_sms?: string | null;
  max_messages?: number;
  customer_id?: string;
}

export const voicemails = {
  listBoxes: (p: { search?: string; limit?: number; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: VoicemailBoxRow[] }>(`/api/voicemail-boxes?${q}`);
  },
  getBox: (id: string) => apiFetch<VoicemailBoxRow>(`/api/voicemail-boxes/${id}`),
  createBox: (body: CreateBoxBody) =>
    apiFetch<VoicemailBoxRow>('/api/voicemail-boxes', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Patch operator-tunable fields. Owner kind/id are immutable on
   * purpose — changing them would orphan messages already in the box.
   */
  updateBox: (id: string, patch: {
    name?: string;
    notify_email?: string | null;
    notify_sms?: string | null;
    max_messages?: number;
  }) =>
    apiFetch<VoicemailBoxRow>(`/api/voicemail-boxes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteBox: (id: string) =>
    apiFetch<void>(`/api/voicemail-boxes/${id}`, { method: 'DELETE' }),

  listMessages: (boxId: string, p: { unread?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.unread) q.set('unread', 'true');
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: VoicemailRow[] }>(`/api/voicemail-boxes/${boxId}/voicemails?${q}`);
  },
  markRead: (id: string) =>
    apiFetch<void>(`/api/voicemails/${id}/read`, { method: 'POST' }),
  markUnread: (id: string) =>
    apiFetch<void>(`/api/voicemails/${id}/unread`, { method: 'POST' }),
  removeMessage: (id: string) =>
    apiFetch<void>(`/api/voicemails/${id}`, { method: 'DELETE' }),

  /**
   * Bulk operations on a set of message ids within a box. Server caps
   * at 500 ids per call; the box scope is enforced server-side so an
   * id that belongs to a different box is silently skipped.
   */
  bulkMarkRead: (boxId: string, ids: string[]) =>
    apiFetch<{ updated: number }>(
      `/api/voicemail-boxes/${boxId}/bulk-mark-read`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),
  bulkMarkUnread: (boxId: string, ids: string[]) =>
    apiFetch<{ updated: number }>(
      `/api/voicemail-boxes/${boxId}/bulk-mark-unread`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),
  bulkDelete: (boxId: string, ids: string[]) =>
    apiFetch<{ deleted: number }>(
      `/api/voicemail-boxes/${boxId}/bulk-delete`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),
  /**
   * Streaming URL for an <audio> tag. <audio> can't set Authorization
   * headers, so the backend's audio endpoint accepts the same 15-minute
   * access-token JWT as a `?token=` query parameter. The token is short-
   * lived so this URL goes stale on the next token refresh — consumers
   * should re-read it whenever the auth store's accessToken changes.
   *
   * Mirrors `recordings.audioUrl()` — same auth contract, different route.
   */
  audioUrl: (id: string, accessToken: string | null | undefined) => {
    const base = `/api/voicemails/${id}/audio`;
    return accessToken ? `${base}?token=${encodeURIComponent(accessToken)}` : base;
  },

  /**
   * Trigger a single-message WAV download via the auth-bearer blob
   * pattern. Same as recordings download — Bearer token in the Auth
   * header (an <a download> can't carry one), then synthesise an
   * <a download> click on a Blob URL.
   */
  downloadOne: async (id: string, suggestedName?: string): Promise<void> => {
    const accessToken = useAuth.getState().accessToken;
    const url = `/api/voicemails/${id}/audio`;
    const res = await fetch(url, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`download_failed_${res.status}`);
    // Force-download (the audio endpoint sets Content-Disposition: inline,
    // which is right for the <audio> player but we want a download click
    // to save). The `download` attribute on <a> overrides inline.
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = suggestedName ?? `voicemail-${id.slice(0, 8)}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  },

  /**
   * Trigger a multi-message ZIP download. Server builds the zip and
   * streams it back; we wrap it in a Blob and synthesise the <a click>.
   * 500 ids per call max (server-enforced).
   */
  downloadZip: async (boxId: string, ids: string[]): Promise<void> => {
    const accessToken = useAuth.getState().accessToken;
    const res = await fetch(`/api/voicemail-boxes/${boxId}/zip`, {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error(`zip_failed_${res.status}`);
    const cd = res.headers.get('Content-Disposition') ?? '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = m?.[1] ?? `voicemails-${new Date().toISOString().slice(0, 10)}.zip`;

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  },
};
